import {
  publishMessageReceivedFromNativeApp,
  subscribeToResponsesFromNativeApp,
} from "./eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {IAnalyticsEvent, ICheckPermissions, IGetAppInstanceId, ToNativeAppEventTypes} from "./eventBus/nativeApp/ToNativeAppEventTypes";
import {logger} from "./log/logger";
import {FromNativeAppEventTypes, IAppInstanceIdResponse, IPermissionsChecked} from "./eventBus/nativeApp/FromNativeAppEventTypes";

/** Holds context for request/response for promise-based communication. Extend this to use it. */
export interface INativeRequestResponseContext {
  context: number;
}

interface IPermissionsCheckRequestResponseMap {
  request: ICheckPermissions;
  response: IPermissionsChecked;
}

interface IAppInstanceIdRequestResponseMap {
  request: IGetAppInstanceId;
  response: IAppInstanceIdResponse;
}

type RequestResponsePairs =
  | IPermissionsCheckRequestResponseMap
  | IAppInstanceIdRequestResponseMap
  // Add more as needed...
  ;

declare global {
  interface Window {
    pgElectronBridge?: {
      sendMessageToHost: (messageString: string) => void;
    };
    nativeBridge: NativeBridge;
    ReactNativeWebView?: {
      postMessage: (messageString: string) => void;
    };
    isElectron?: boolean;
    /** @deprecated Use pgElectronBridge instead. Once all desktop apps use pgElectronBridge, remove ipcRenderer. */
    ipcRenderer?: {
      send: (channel: string, messageString: string) => void;
    }
  }
}

/**
 * Subscribe to events to receive messages sent from native container.
 * Call functions directly on NativeBridge to send messages to native container. (no need for pub/sub)
 */
class NativeBridge{
  isRunningInReactNative = window.ReactNativeWebView !== undefined;
  isRunningInElectron = window.isElectron !== undefined;

  isRunningInNativeContainer = this.isRunningInElectron || this.isRunningInReactNative;
  routePathsHandledByNative: string[] = []; //  "/sign-in" is the only path possible, but probably not needed...  consider removing all this functionality at the end of the project, when we know we don't have any native paths (initially we wanted native screens for guitar app, since it didn't need midi, and the app store wont let you just wrap a web app)
  /**
   * Native will call this function anytime it wants to send a message down.
   * @param messageString - JSON.stringified object
   */
  handleMessageFromNative(messageString: string){
    logger.nativeToWebviewMessage.info({
      message: `Native --> webview`,
      data: [
        { key: "messageString", value: messageString },
      ]
    });

    const message = JSON.parse(messageString);
    publishMessageReceivedFromNativeApp(message);
  }

  /**
   * Native app listens for onMessage from the web view.
   */
  sendMessageToNative(message: ToNativeAppEventTypes, context?: number) {
    const messageString = context != undefined ? JSON.stringify({...message, context}) : JSON.stringify(message);

    logger.webviewToNativeMessage.info({
      message: `Webview --> native:`,
      data: [
        { key: "type", value: message.type },
        { key: "context, if", value: context?.toString() ?? "n/a" },
        { key: "message", value: JSON.stringify(message) },
      ]
    });

    if(window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(messageString);
    }

    if(window.ipcRenderer) {
      // window.ipcRenderer is the old way to send messages to the host (native app) in Electron.
      // Once we no longer have these versions of the desktop app in production use, we can get rid of window.ipcRenderer.
      // - Windows 3.0.43
      // - Mac 3.0.42
      window.ipcRenderer.send('message-from-web-app', messageString);
    }

    if (typeof window?.pgElectronBridge?.sendMessageToHost === 'function') {
      // This is the new way to send messages to the host (native app) in Electron.
      window.pgElectronBridge.sendMessageToHost(messageString);
    }
  }

  /**
   * Send an analytics event to the native app, which will forward it to the analytics provider (Firebase, etc.)
   * This should only be called from the in-app analytics service.
   */
  sendAnalyticsEvent(eventName: IAnalyticsEvent["eventName"],  parameters: IAnalyticsEvent["parameters"]){
    this.sendMessageToNative({type: "analytics.event", eventName, parameters});
  }

  async checkPermissions(): Promise<IPermissionsChecked["permissions"]> {
    return new Promise((resolve) => {
      this.#sendRequestToNative<IPermissionsCheckRequestResponseMap>({
        type: "permissionsCheck_requested",
      }, 2000).then(
        // Success handler
        (response) => {
          resolve(response.permissions)
        },
        // Reject handler
        (reason) => {
          if (reason !== 'timeout') {
            throw reason;
          }

          resolve({
            microphone: "timeout",
          });
        }
      );
    });
  }

  async getAppInstanceId(): Promise<string | null> {
    return new Promise((resolve) => {
      this.#sendRequestToNative<IAppInstanceIdRequestResponseMap>({
        type: "appInstanceId_requested",
      }, 2000).then(
        // Success handler
        (response) => {
          resolve(response.appInstanceId)
        },
        // Reject handler
        (reason) => {
          if (reason !== 'timeout') {
            throw reason;
          }

          resolve(null);
        }
      );
    });
  }

  #contextForRequest = 0;

  #sendRequestToNative<T extends RequestResponsePairs>(request: T["request"], timeoutMs: number = 100): Promise<T["response"]>{
    return new Promise((resolve, reject: (reason: "timeout") => void) => {
      // Context is used to map the response to a previous request.
      const context = this.#contextForRequest++;

      this.sendMessageToNative(request, context);

      // If the response hasn't been received in a small amount of time, consider it as failed.
      // The request/response duration has been seen to range from 60-250ms
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject("timeout");
      }, timeoutMs); // https://www.nngroup.com/articles/response-times-3-important-limits/

      const unsubscribe = subscribeToResponsesFromNativeApp((response: FromNativeAppEventTypes & INativeRequestResponseContext) => {
        if (response.context === context) {
          unsubscribe();
          clearTimeout(timeoutId);
          resolve(response as T["response"]);
        }
      });
    });
  }

  /**
   * When the web-app wants to render a page, it'll call this function so the native app actually displays the web-app.
   */
  tellNativeToRouteToWebApp(){
    this.sendMessageToNative({type: "routeToNative", to:"/web-app"});
  }

  tellNativeToRouteToRoutePath(routePath: string){
    this.sendMessageToNative({type: "routeToNative", to: routePath});
  }

  tellNativeTheAppVersion(version: string){
    this.sendMessageToNative({type:"appVersion", version});
  }

  tellNativeToOpenUrlInBrowser(url: string){
    this.sendMessageToNative({type: "openUrlInBrowser", url});
  }

  tellNativeToPromptTheUserToRateTheApp(){
    this.sendMessageToNative({type: "promptUserToRateTheApp"});
  }

  tellNativeRouteChanged(locationPath: string, routePath: string){
    this.sendMessageToNative({type: "routeChanged", locationPath, routePath});
  }
}

const nativeBridge = new NativeBridge();

//@ts-ignore - expose the nativeBridge to the native, as it will call window.nativeBridge.handleMessageFromNative(...)
window.nativeBridge = nativeBridge;

export default nativeBridge;
